import React from 'react';
import { Router, Redirect } from '@reach/router';
import { compose, lifecycle } from 'recompose';
import {
  ErrorMessage,
  LoadingMessage,
  C,
  selectServicesKappSlug,
} from '@kineticdata/bundle-common';
import { connect } from './redux/store';
import { actions } from './redux/modules/servicesApp';
import { actions as externalOfferingsActions } from './redux/modules/externalOfferings';
import { parse } from 'query-string';

import { PageTitle } from './components/shared/PageTitle';
import { Categories } from './components/Categories';
import { Catalog } from './components/catalog/Catalog';
import { Portfolio } from './components/catalog/Portfolio';
import { Service } from './components/catalog/Service';
import { Appendix } from './components/catalog/Appendix';
import { Favorites } from './components/favorites/Favorites';
import { Page } from './components/page/Page';
import { FormContainer } from './components/form/FormContainer';
import {
  RequestList,
  RequestListWrapper,
} from './components/requests/RequestList';
import { Request } from './components/requests/Request';
import {
  ApprovalList,
  ApprovalListWrapper,
} from './components/approvals/ApprovalList';
import { Approval } from './components/approvals/Approval';
import { ActionList, ActionListWrapper } from './components/actions/ActionList';
import { Action } from './components/actions/Action';
import { Settings } from './components/settings/Settings';
import { I18n, CoreForm } from '@kineticdata/react';

/*****************************************************************************
 *** PRIVATE APP
 *****************************************************************************/

const RouterGroup = ({ children }) => children;
const NoAgreementsPage = connect(state => ({
  kappSlug: selectServicesKappSlug(state),
}))(({ kappSlug }) => (
  <div className="page-container">
    <div className="page-panel">
      <div className="portal-page__wrapper">
        <CoreForm kapp={kappSlug} form={C.FORM_SLUG_NO_ACCESS} />
      </div>
    </div>
  </div>
));

const SubmissionRedirect = props => (
  <Redirect
    to={`${props.appLocation}/requests/request/${props.id}/${
      props.location.search.includes('review') ? 'review' : 'activity'
    }`}
    noThrow
  />
);

const AppComponent = props => {
  return props.render({
    main: props.error ? (
      <ErrorMessage
        title="Unexpected Error"
        message="Sorry, an unexpected error has occurred!"
      />
    ) : !props.init ? (
      <LoadingMessage />
    ) : props.pageRedirect ? (
      <Redirect to={`${props.appLocation}/${props.pageRedirect}`} noThrow />
    ) : (
      <I18n>
        <main className="package-layout package-layout--services">
          <PageTitle parts={['Loading...']} />
          <Router>
            <SubmissionRedirect
              path="submissions/:id"
              appLocation={props.appLocation}
            />
            <SubmissionRedirect
              path="forms/:formSlug/submissions/:id"
              appLocation={props.appLocation}
            />

            {/* Deprecated UI - to be removed in the future */}
            {props.profile.isSMO ? (
              <RouterGroup path="servicecatalog">
                <Catalog path="/" />
                <Portfolio path="portfolios/:portfolioId" />
                <Service path="services/:serviceId" />
                <Service path="portfolios/:portfolioId/services/:serviceId" />
                <Appendix path="appendices/:appendixId" />
                <Appendix path="services/:serviceId/appendices/:appendixId" />
                <Appendix path="portfolios/:portfolioId/services/:serviceId/appendices/:appendixId" />
                <FormContainer path="services/:serviceId/forms/:formSlug" />
                <FormContainer path="services/:serviceId/forms/:formSlug/:submissionId" />
                <FormContainer path="portfolios/:portfolioId/services/:serviceId/forms/:formSlug" />
                <FormContainer path="portfolios/:portfolioId/services/:serviceId/forms/:formSlug/:submissionId" />
                <FormContainer path="categories/:portfolioId/services/:serviceId/forms/:formSlug" />
                <FormContainer path="categories/:portfolioId/services/:serviceId/forms/:formSlug/:submissionId" />
              </RouterGroup>
            ) : null}

            {props.profile.validEauthAssuranceLevel ? (
              <RouterGroup default>
                <Categories path="/" />
                <Redirect from="categories" to=".." noThrow />
                <Categories path="categories/:categorySlug" />
                <FormContainer path="forms/:formSlug" />
                <FormContainer path="forms/:formSlug/:submissionId" />
                <FormContainer path="categories/:categorySlug/:formSlug" />
                <FormContainer path="categories/:categorySlug/:formSlug/:submissionId" />
                <FormContainer path="frameless/forms/:formSlug" />
                <FormContainer path="frameless/forms/:formSlug/:submissionId" />
                <Favorites path="favorites" />
                <RequestListWrapper path="requests">
                  <Redirect from="/" to="Open" noThrow />
                  <RequestList path=":type" />
                  <FormContainer path="request/:submissionId" />
                  <FormContainer path=":type/request/:submissionId" />
                  <Request path="request/:submissionId/:mode" />
                  <Request path=":type/request/:submissionId/:mode" />
                </RequestListWrapper>
                <ApprovalListWrapper path="approvals">
                  <Redirect from="/" to="Pending" noThrow />
                  <ApprovalList path=":type" />
                  <Approval path="approval/:submissionId" />
                  <Approval path=":type/approval/:submissionId" />
                  <Approval path="approval/:submissionId/:mode" />
                  <Approval path=":type/approval/:submissionId/:mode" />
                </ApprovalListWrapper>
                <ActionListWrapper path="actions">
                  <Redirect from="/" to="Pending" noThrow />
                  <ActionList path=":type" />
                  <Action path="action/:submissionId" />
                  <Action path=":type/action/:submissionId" />
                  <Action path="action/:submissionId/:mode" />
                  <Action path=":type/action/:submissionId/:mode" />
                </ActionListWrapper>
                <Page path="page/:pageSlug" />
                <Settings path="settings/*" />
              </RouterGroup>
            ) : (
              <RouterGroup default>
                <NoAgreementsPage default />
              </RouterGroup>
            )}
          </Router>
        </main>
      </I18n>
    ),
  });
};

const mapStateToProps = state => {
  const params = parse(
    `${state.router.location.pathname}${state.router.location.search}`.replace(
      /.*?\?/,
      '?',
    ),
  );
  let pageRedirect = null;
  if (params.page) {
    switch (params.page) {
      case 'submission':
        pageRedirect = params.id
          ? `requests/request/${params.id}/activity`
          : 'requests';
        break;
      case 'submissionReview':
        pageRedirect = params.id
          ? `requests/request/${params.id}/review`
          : 'requests';
        break;
      case 'approval':
        pageRedirect = params.id
          ? `approvals/approval/${params.id}`
          : 'approvals';
        break;
      case 'action':
        pageRedirect = params.id ? `actions/action/${params.id}` : 'actions';
        break;
      case 'page':
        pageRedirect = params.slug ? `page/${params.slug}` : null;
        break;
      case 'requests':
        pageRedirect = params.mode ? `requests/${params.mode}` : 'requests';
        break;
      default:
        break;
    }
  }

  return {
    profile: state.app.profile,
    init: state.servicesApp.init,
    loading: state.servicesApp.loading,
    error: state.servicesApp.error,
    pathname: state.router.location.pathname,
    appLocation: state.app.location,
    pageRedirect,
  };
};

const mapDispatchToProps = {
  fetchAppDataRequest: actions.fetchAppDataRequest,
  fetchExternalOfferings:
    externalOfferingsActions.fetchExternalOfferingsRequest,
};

export const App = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
  lifecycle({
    componentDidMount() {
      this.props.fetchAppDataRequest();
      this.props.fetchExternalOfferings();
    },
  }),
)(AppComponent);

/*****************************************************************************
 *** PUBLIC APP
 *****************************************************************************/

export const PublicAppComponent = props => {
  return props.render({
    main: (
      <I18n>
        <main className="package-layout package-layout--services">
          <PageTitle parts={['Loading...']} />
          <Router>
            <FormContainer path="forms/:formSlug" />
            <FormContainer path="forms/:formSlug/:submissionId" />
            <FormContainer path="frameless/forms/:formSlug" />
            <FormContainer path="frameless/forms/:formSlug/:submissionId" />
            <FormContainer path="submissions/:submissionId" />
            <Redirect from="*" to={props.authRoute} noThrow />
          </Router>
        </main>
      </I18n>
    ),
  });
};

const mapStateToPropsPublic = state => ({
  authRoute: state.app.authRoute,
});

export const PublicApp = compose(connect(mapStateToPropsPublic))(
  PublicAppComponent,
);
